import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Heart, ShoppingCart, Loader2, Minus, Plus, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/shop/ProductCard';
import { productService } from '../services/product.service';
import { useCartStore } from '../store/cart.store';
import { useToast } from '../components/ui/ToastProvider';

export const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { addItem } = useCartStore();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = React.useState(1);
  const [selectedImage, setSelectedImage] = React.useState<string | undefined>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['product', slug],
    queryFn: () => productService.getProductBySlug(slug!),
    enabled: !!slug,
  });

  const productId = data?.product.id;
  const { data: wishlistState } = useQuery({
    queryKey: ['wishlist-check', productId],
    queryFn: () => productService.checkWishlist(productId!),
    enabled: Boolean(productId && localStorage.getItem('edytech_access_token')),
  });

  const wishlistMutation = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error('Product not loaded');
      if (wishlistState?.inWishlist) {
        await productService.removeFromWishlist(productId);
        return false;
      }
      await productService.addToWishlist(productId);
      return true;
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['wishlist-check', productId] });
      queryClient.invalidateQueries({ queryKey: ['wishlist'] });
      showToast(saved ? `${data!.product.name} saved to your wishlist` : 'Removed from wishlist', 'success');
    },
    onError: (error: unknown) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      showToast(status === 401 ? 'Sign in to save products to your wishlist' : 'Unable to update your wishlist', 'error');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !data?.product) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-2xl font-bold">Product not found</h1>
        <Link to="/shop" className="mt-4 inline-block font-medium text-primary hover:underline">
          Back to shop
        </Link>
      </div>
    );
  }

  const { product, images, variants, reviews } = data;
  const featuredImage = images?.find((img) => img.isFeatured) || images?.[0];
  const activeImage = selectedImage || featuredImage?.url;
  const discount = product.comparePrice
    ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
    : 0;
  const averageRating = product.averageRating || 0;

  const handleAddToCart = () => {
    if (product.stock <= 0) {
      showToast('Sorry, this product is out of stock', 'error');
      return;
    }
    for (let item = 0; item < quantity; item += 1) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: featuredImage?.url || '/placeholder-product.png',
      });
    }
    showToast(`${product.name} added to cart`, 'success');
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
        <div>
          <div className="relative flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-[rgb(var(--border))]">
          {activeImage ? (
            <img src={activeImage} alt={product.name} className="h-full w-full object-cover" />
          ) : (
            <span className="text-sm text-[rgb(var(--light-secondary))]">Product image</span>
          )}
          {discount > 0 && (
            <span className="absolute top-2 right-2 rounded-full bg-red-500 px-2 py-1 text-xs font-bold text-white">
              -{discount}%
            </span>
          )}
          </div>
          {images && images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto">
              {images.map((image) => (
                <button
                  key={image.id}
                  type="button"
                  onClick={() => setSelectedImage(image.url)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${activeImage === image.url ? 'border-primary' : 'border-transparent'}`}
                  aria-label={`View ${image.alt || product.name}`}
                >
                  <img src={image.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-primary">
            {product.categoryId ? `Category #${product.categoryId}` : ''}
          </p>
          <h1 className="mt-2 text-3xl font-bold text-[rgb(var(--foreground))]">{product.name}</h1>
          {product.sku && <p className="mt-1 text-xs text-[rgb(var(--light-secondary))]">SKU: {product.sku}</p>}

          {averageRating > 0 && (
            <div className="mt-2 flex items-center space-x-1 text-sm text-yellow-500">
              <Star className="h-4 w-4 fill-current" />
              <span>{averageRating.toFixed(1)}</span>
              {product.reviewCount > 0 && (
                <span className="text-[rgb(var(--light-secondary))]">({product.reviewCount} reviews)</span>
              )}
            </div>
          )}

          <p className="mt-4 text-[rgb(var(--light-secondary))]">
            {product.description || product.shortDescription}
          </p>

          <div className="mt-6 flex items-baseline space-x-3">
            <span className="text-2xl font-bold text-primary">GHS {(product.price / 100).toFixed(2)}</span>
            {product.comparePrice && (
              <span className="text-[rgb(var(--light-secondary))] line-through">
                GHS {(product.comparePrice / 100).toFixed(2)}
              </span>
            )}
          </div>

          <p className={`mt-2 text-sm font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-500'}`}>
            {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
          </p>

          {variants && variants.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold text-[rgb(var(--foreground))]">Options</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {variants.map((variant) => (
                  <span key={variant.id} className="rounded-full border border-[rgb(var(--border))] px-3 py-1 text-sm">
                    {variant.name}
                    {variant.price ? ` · GHS ${(variant.price / 100).toFixed(2)}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="mt-6 border-t border-[rgb(var(--border))] pt-5">
              <h2 className="text-sm font-semibold text-[rgb(var(--foreground))]">Specifications</h2>
              <dl className="mt-3 space-y-2 text-sm">
                {Object.entries(product.specifications).map(([name, value]) => (
                  <div key={name} className="flex justify-between gap-4">
                    <dt className="text-[rgb(var(--light-secondary))]">{name}</dt>
                    <dd className="text-right font-medium">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <div className="mt-8 space-y-2">
            {['Genuine and tested parts', '12-month warranty', 'Fast delivery across the country'].map((item) => (
              <div key={item} className="flex items-center space-x-2 text-sm">
                <CheckCircle className="h-4 w-4 text-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <div className="flex items-center rounded-lg border border-[rgb(var(--border))]">
              <button type="button" onClick={() => setQuantity((value) => Math.max(1, value - 1))} className="p-3" aria-label="Decrease quantity">
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-8 text-center text-sm font-semibold">{quantity}</span>
              <button type="button" onClick={() => setQuantity((value) => Math.min(product.stock, value + 1))} className="p-3" aria-label="Increase quantity" disabled={quantity >= product.stock}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button onClick={handleAddToCart} disabled={product.stock <= 0} className="flex items-center space-x-2">
              <ShoppingCart className="h-4 w-4" />
              <span>Add to Cart</span>
            </Button>
            <Button variant="secondary" onClick={() => wishlistMutation.mutate()} disabled={wishlistMutation.isPending} aria-label="Toggle product wishlist">
              <Heart className={`mr-2 h-4 w-4 ${wishlistState?.inWishlist ? 'fill-red-500 text-red-500' : ''}`} />
              <span>{wishlistState?.inWishlist ? 'Saved' : 'Save'}</span>
            </Button>
            <Link to="/shop">
              <Button variant="secondary">Continue Shopping</Button>
            </Link>
          </div>
        </div>
      </div>

      {reviews && reviews.length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-[rgb(var(--foreground))]">Reviews</h2>
          <div className="mt-4 space-y-4">
            {reviews.map((review) => (
              <div key={review.id} className="rounded-xl border border-[rgb(var(--border))] p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-semibold">{review.user?.name || 'Customer'}</span>
                    <span className="flex items-center text-yellow-500">
                      <Star className="h-3 w-3 fill-current" />
                      <span className="ml-1 text-xs">{review.rating}</span>
                    </span>
                  </div>
                  <span className="text-xs text-[rgb(var(--light-secondary))]">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {review.title && <p className="mt-2 text-sm font-semibold">{review.title}</p>}
                <p className="mt-1 text-sm text-[rgb(var(--light-secondary))]">{review.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.related && data.related.length > 0 && (
        <section className="mt-14 border-t border-[rgb(var(--border))] pt-8">
          <h2 className="text-2xl font-bold text-[rgb(var(--foreground))]">You may also like</h2>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {data.related.map((relatedProduct) => (
              <ProductCard key={relatedProduct.id} product={relatedProduct} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};